// Single `knowledge` Vuex module shared by all tabs: only one tab is mounted at a time and
// holds the current view's results; filters/page are owned by the page and passed into FETCH.
import { Module } from 'vuex'

import { RootState } from '~/store'
import {
  DemoteAnalysisMemoryFromOwnerPayload,
  PromoteAnalysisMemoryToOwnerPayload
} from '~/types/types'
import {
  KnowledgeChunk,
  KnowledgeChunkModuleActions,
  KnowledgeChunkModuleGetters,
  KnowledgeChunkModuleMutations,
  KnowledgeChunkModuleState,
  KnowledgeChunkType,
  KnowledgeFilterOption,
  KnowledgeFilters,
  KnowledgeScope,
  KNOWLEDGE_PAGE_SIZE
} from '~/utils/knowledge/types'
import { Repository } from '~/types/types'
import { resolveNodes } from '~/utils/array'
import RepoMemoriesQuery from '~/apollo/queries/repository/knowledge/repoMemories.gql'
import OwnerMemoriesQuery from '~/apollo/queries/owner/knowledge/ownerMemories.gql'
import OwnerRepoOptionsQuery from '~/apollo/queries/owner/knowledge/ownerRepoOptions.gql'
import DeleteAnalysisMemoryMutation from '~/apollo/mutations/knowledge/deleteAnalysisMemory.gql'
import PromoteAnalysisMemoryToOwnerMutation from '~/apollo/mutations/knowledge/promoteAnalysisMemoryToOwner.gql'
import DemoteAnalysisMemoryFromOwnerMutation from '~/apollo/mutations/knowledge/demoteAnalysisMemoryFromOwner.gql'

interface ScopedArgs {
  chunkType: KnowledgeChunkType
  scope: KnowledgeScope
  provider: string
  owner: string
  name?: string
  filters: KnowledgeFilters
  page: number
  /** Server-side ordering key (per tab); omitted = backend default (-created). */
  sort?: string
}

const knowledgeStoreModule: Module<KnowledgeChunkModuleState, RootState> = {
  namespaced: true,
  state: (): KnowledgeChunkModuleState => ({
    chunks: [],
    totalCount: 0,
    loading: false,
    error: null
  }),
  mutations: {
    [KnowledgeChunkModuleMutations.SET_CHUNKS](state, chunks: Array<KnowledgeChunk>) {
      state.chunks = chunks
    },
    [KnowledgeChunkModuleMutations.SET_TOTAL_COUNT](state, totalCount: number) {
      state.totalCount = totalCount
    },
    [KnowledgeChunkModuleMutations.SET_LOADING](state, loading: boolean) {
      state.loading = loading
    },
    [KnowledgeChunkModuleMutations.SET_ERROR](state, error: Error | null) {
      state.error = error
    }
  },
  getters: {
    [KnowledgeChunkModuleGetters.IS_EMPTY]: (state) => !state.loading && state.totalCount === 0
  },
  actions: {
    async [KnowledgeChunkModuleActions.FETCH](
      { commit },
      { chunkType, scope, provider, owner, name, filters, page, sort }: ScopedArgs
    ) {
      commit(KnowledgeChunkModuleMutations.SET_LOADING, true)
      commit(KnowledgeChunkModuleMutations.SET_ERROR, null)
      try {
        const { content, source, category, repository } = filters
        const shared = {
          chunkType,
          sourceType: source || undefined,
          content: content || undefined,
          categoryIn: category.length ? category : undefined,
          repositoryId: repository?.value || undefined,
          first: KNOWLEDGE_PAGE_SIZE,
          after: this.$getGQLAfter(page, KNOWLEDGE_PAGE_SIZE),
          sort
        }
        const isOwner = scope === KnowledgeScope.Owner
        const response = await this.$fetchGraphqlData(
          isOwner ? OwnerMemoriesQuery : RepoMemoriesQuery,
          isOwner ? { login: owner, provider, ...shared } : { provider, owner, name, ...shared },
          true
        )
        const connection = isOwner
          ? response?.data?.owner?.memories
          : response?.data?.repository?.memories
        commit(KnowledgeChunkModuleMutations.SET_CHUNKS, resolveNodes(connection) as Array<KnowledgeChunk>)
        commit(KnowledgeChunkModuleMutations.SET_TOTAL_COUNT, connection?.totalCount ?? 0)
      } catch (e) {
        // Clear results so a failed tab can't show another tab's stale chunks (shared store).
        commit(KnowledgeChunkModuleMutations.SET_CHUNKS, [])
        commit(KnowledgeChunkModuleMutations.SET_TOTAL_COUNT, 0)
        commit(KnowledgeChunkModuleMutations.SET_ERROR, e as Error)
        throw e
      } finally {
        commit(KnowledgeChunkModuleMutations.SET_LOADING, false)
      }
    },

    async [KnowledgeChunkModuleActions.FETCH_REPO_OPTIONS](
      _ctx,
      { provider, owner, query }: { provider: string; owner: string; query?: string }
    ): Promise<Array<KnowledgeFilterOption>> {
      const response = await this.$fetchGraphqlData(OwnerRepoOptionsQuery, {
        login: owner,
        provider,
        first: 20,
        query: query || undefined
      })
      const repos = resolveNodes(response?.data?.owner?.repositories) as Array<Repository>
      return repos.map((repo) => ({ value: repo.id, label: repo.name }))
    },

    async [KnowledgeChunkModuleActions.DELETE](_ctx, { id }: { id: string }): Promise<boolean> {
      const response = await this.$applyGraphqlMutation(DeleteAnalysisMemoryMutation, {
        input: { memoryId: id }
      })
      return Boolean(response?.data?.deleteAnalysisMemory?.ok)
    },

    async [KnowledgeChunkModuleActions.PROMOTE](
      _ctx,
      { id, note }: { id: string; note?: string }
    ): Promise<PromoteAnalysisMemoryToOwnerPayload> {
      const response = await this.$applyGraphqlMutation(PromoteAnalysisMemoryToOwnerMutation, {
        input: { memoryId: id, note }
      })
      return response?.data?.promoteAnalysisMemoryToOwner
    },

    async [KnowledgeChunkModuleActions.DEMOTE](
      _ctx,
      { id, note }: { id: string; note?: string }
    ): Promise<DemoteAnalysisMemoryFromOwnerPayload> {
      const response = await this.$applyGraphqlMutation(DemoteAnalysisMemoryFromOwnerMutation, {
        input: { memoryId: id, note }
      })
      return response?.data?.demoteAnalysisMemoryFromOwner
    }
  }
}

export default knowledgeStoreModule
